import type { LicenseTypeConfigurationResponseSchema } from "@/hera-api/client";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import { cn } from "@/lib/utils";
import {
  type AddressDetails,
  type AddressDetailsFormData,
  AddressSchema,
  PropertyAddressSchema,
} from "@/types/address";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { FormFieldWrapper } from "../../ui/form-field-wrapper";
import { Input } from "../../ui/input";
import { SuggestionList } from "../../ui/suggestion-list";
import { useAppForm } from "../../ui/tanstack-form";
import { FeeInformation } from "./fee-information";

interface AddressDetailsFormProps {
  initialData?: AddressDetails;
  onPrevious?: () => void;
  onNext: (data: AddressDetailsFormData) => Promise<void>;
  stepNumber: number;
  configurationData?: LicenseTypeConfigurationResponseSchema;
}

export function AddressDetailsForm({
  initialData,
  onNext,
  onPrevious,
  stepNumber,
  configurationData,
}: AddressDetailsFormProps) {
  const [placesAutoCompleteInput, setPlacesAutoCompleteInput] = useState("");
  const { suggestions } = usePlacesAutocomplete(placesAutoCompleteInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  const form = useAppForm({
    defaultValues: {
      address1: initialData?.address1 ?? "",
      address2: initialData?.address2 ?? "",
      city: initialData?.city ?? "",
      state: initialData?.state ?? "",
      zipcode: initialData?.zipcode ?? "",
      apn: initialData?.apn ?? "",
    },
    validators: {
      onSubmit: PropertyAddressSchema,
    },
    onSubmit: async ({ value }) => {
      await onNext(value);
    },
  });

  const handlePrevious = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onPrevious) {
      onPrevious();
    }
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const handleSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) {
      return;
    }
    await place.fetchFields({
      fields: ["addressComponents"],
    });

    if (!place?.addressComponents) {
      return;
    }

    const { address1, address2, city, state, zipcode } =
      parseGoogleAddressComponents(place.addressComponents);

    form.setFieldValue("address1", address1);
    form.validateField("address1", "change");
    form.setFieldValue("address2", address2);
    form.validateField("address2", "change");
    form.setFieldValue("city", city);
    form.validateField("city", "change");
    form.setFieldValue("state", state);
    form.validateField("state", "change");
    form.setFieldValue("zipcode", zipcode);
    form.validateField("zipcode", "change");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step {stepNumber}: Property Address</CardTitle>
      </CardHeader>

      <CardContent>
        <form.AppForm>
          <form className="space-y-6" onSubmit={handleNext}>
            <form.AppField
              name="address1"
              validators={{
                onChange: AddressSchema.shape.address1,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Address Line 1">
                  <SuggestionList
                    closeOnBlur
                    suggestions={suggestions}
                    renderSuggestion={(suggestion) => (
                      <span>{suggestion.placePrediction?.text?.text}</span>
                    )}
                    getSuggestionKey={(suggestion) =>
                      suggestion.placePrediction?.placeId ?? ""
                    }
                    onSuggestionSelect={handleSuggestionClick}
                  >
                    <Input
                      id={field.name}
                      placeholder="Enter your street address"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setPlacesAutoCompleteInput(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                    />
                  </SuggestionList>
                </FormFieldWrapper>
              )}
            </form.AppField>
            <form.AppField
              name="address2"
              validators={{
                onChange: AddressSchema.shape.address2,
              }}
            >
              {(field) => (
                <FormFieldWrapper
                  field={field}
                  label="Address Line 2 (Optional)"
                >
                  <Input
                    id="address2"
                    placeholder="Apartment, suite, unit, etc."
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                </FormFieldWrapper>
              )}
            </form.AppField>
            <div className="grid md:grid-cols-3 gap-4 items-start">
              <form.AppField
                name="city"
                validators={{
                  onChange: AddressSchema.shape.city,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="City">
                    <Input
                      id="city"
                      placeholder="Enter your city"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="state"
                validators={{
                  onChange: AddressSchema.shape.state,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="State">
                    <Input
                      id="state"
                      placeholder="XX"
                      maxLength={2}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="zipcode"
                validators={{
                  onChange: AddressSchema.shape.zipcode,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="ZIP Code">
                    <Input
                      id="zipcode"
                      placeholder="XXXXX or XXXXX-XXXX"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
            </div>
            <form.AppField
              name="apn"
              validators={{
                onChange: PropertyAddressSchema.shape.apn,
              }}
            >
              {(field) => (
                <FormFieldWrapper
                  field={field}
                  label="APN / Parcel Number (Optional)"
                >
                  <Input
                    id="apn"
                    placeholder="If you have it, matching parcels will be listed first"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                </FormFieldWrapper>
              )}
            </form.AppField>
          </form>
        </form.AppForm>

        {configurationData && (
          <div className="mt-6">
            <FeeInformation configurationData={configurationData} />
          </div>
        )}
      </CardContent>
      <CardFooter
        className={cn("flex justify-between", onPrevious || "flex-row-reverse")}
      >
        {onPrevious && (
          <Button variant="outline" onClick={handlePrevious}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
        )}
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <Button
              className="disabled:opacity-20 disabled:cursor-not-allowed"
              onClick={handleNext}
              disabled={!canSubmit}
            >
              {isSubmitting ? (
                "Saving..."
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </form.Subscribe>
      </CardFooter>
    </Card>
  );
}
